import { yupResolver } from '@hookform/resolvers/yup';
import SyncIcon from '@mui/icons-material/Sync';
import {
	Button,
	Drawer,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import {
	DesktopDateTimePicker,
	LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useAuthContext } from '../../../../../context/auth';
import { dayjs } from '../../../../../date-util';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
} from '../../../../fastForm/data/constants';
import ScriptLogsAPI, { LogEvent } from '../../api/scriptLogs';
import {
	SCRIPT_LOGS_CLEAR_BUTTON_LABEL,
	SCRIPT_LOGS_END_DATE_INPUT_LABEL,
	SCRIPT_LOGS_FETCH_MORE_TEXT,
	SCRIPT_LOGS_HAS_MORE_TEXT,
	SCRIPT_LOGS_HEADER,
	SCRIPT_LOGS_LAST_UPDATED_LABEL,
	SCRIPT_LOGS_NO_MORE_TEXT,
	SCRIPT_LOGS_REFRESHING_LABEL,
	SCRIPT_LOGS_REFRESH_BUTTON_LABEL,
	SCRIPT_LOGS_SEARCH_BUTTON_LABEL,
	SCRIPT_LOGS_SEARCH_INPUT_LABEL,
	SCRIPT_LOGS_START_DATE_INPUT_LABEL,
	SCRIPT_LOGS_SUBHEADER,
	SCRIPT_LOGS_SUBHEADER_TIMESTAMP,
} from '../../data/constants';
import ScriptLogRow from './ScriptLogRow';
import ScriptLogRowSkeleton from './ScriptLogRowSkeleton';
import ScriptLogsNested from './ScriptLogsNested';

const useFilterParams = () => {
	const locationSearch = useLocation().search;
	const params = new URLSearchParams(locationSearch);

	return {
		message: params.get('message') ?? '',
		startDate: params.get('startDate') ?? '',
		endDate: params.get('endDate') ?? '',
	};
};

interface IFormInputs {
	message: string;
	startDate: number;
	endDate: number;
}

interface IFormValues {
	message: string;
	startDate: dayjs.Dayjs;
	endDate: dayjs.Dayjs;
}

interface FilterScriptLogsProps extends IFormInputs {
	onApply: (data: IFormInputs) => void;
	refetch: () => void;
}

const FilterScriptLogs = (props: FilterScriptLogsProps) => {
	const { onApply, startDate, endDate, message, refetch } = props;
	const navigate = useNavigate();
	const location = useLocation();
	const { handleSubmit, control, reset } = useForm<IFormValues>({
		defaultValues: {
			startDate: startDate ? dayjs(startDate) : null,
			endDate: endDate ? dayjs(endDate) : null,
			message,
		},
		resolver: yupResolver(
			yup.object().shape({
				message: yup.string().nullable(),
				startDate: yup.date().nullable(),
				endDate: yup.date().nullable(),
			}),
		),
	});

	const onSubmit: SubmitHandler<IFormValues> = (data) => {
		const { message, startDate, endDate } = data;
		const request = {} as IFormInputs;

		if (message) {
			request.message = message;
		}
		if (startDate) {
			request.startDate = startDate.valueOf();
		}
		if (endDate) {
			request.endDate = endDate.valueOf();
		}

		onApply(request);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Stack
				direction='row'
				justifyContent='space-between'
				alignItems='flex-end'
			>
				<Stack
					alignItems={{
						xs: 'flex-start',
						sm: 'flex-start',
						md: 'flex-start',
						lg: 'center',
					}}
					component='form'
					direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
					spacing={1}
					onSubmit={handleSubmit(onSubmit)}
				>
					<Controller
						name='message'
						control={control}
						render={({ field }) => (
							<TextField
								label={SCRIPT_LOGS_SEARCH_INPUT_LABEL()}
								variant='outlined'
								size='small'
								sx={{ backgroundColor: 'white.lightest', width: '233px' }}
								{...field}
							/>
						)}
					/>

					<Controller
						name='startDate'
						control={control}
						render={({ field }) => (
							<DesktopDateTimePicker
								inputFormat={DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.SECOND]}
								label={SCRIPT_LOGS_START_DATE_INPUT_LABEL()}
								disableMaskedInput={false}
								{...field}
								renderInput={(params) => (
									<TextField
										variant='outlined'
										size='small'
										sx={{ backgroundColor: 'white.lightest', width: '250px' }}
										{...params}
									/>
								)}
							/>
						)}
					/>
					<Controller
						name='endDate'
						control={control}
						render={({ field }) => (
							<DesktopDateTimePicker
								inputFormat={DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.SECOND]}
								label={SCRIPT_LOGS_END_DATE_INPUT_LABEL()}
								{...field}
								renderInput={(params) => (
									<TextField
										variant='outlined'
										size='small'
										sx={{ backgroundColor: 'white.lightest', width: '250px' }}
										{...params}
									/>
								)}
							/>
						)}
					/>
					<Button variant='contained' color='secondary' type='submit'>
						{SCRIPT_LOGS_SEARCH_BUTTON_LABEL()}
					</Button>
					<Button
						variant='text'
						color='neutralButton'
						onClick={() => {
							reset({
								message: '',
								startDate: null,
								endDate: null,
							});
							navigate(location.pathname, { replace: true });
						}}
					>
						{SCRIPT_LOGS_CLEAR_BUTTON_LABEL()}
					</Button>
				</Stack>{' '}
				<Button
					variant='outlined'
					color='neutralButton'
					onClick={() => refetch()}
					startIcon={<SyncIcon />}
				>
					{SCRIPT_LOGS_REFRESH_BUTTON_LABEL()}
				</Button>
			</Stack>
		</LocalizationProvider>
	);
};

export default function ScriptLogs() {
	const { tenantHostname } = useParams();
	const { ref, inView } = useInView();
	const navigate = useNavigate();
	const location = useLocation();
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const { startDate, endDate, message } = useFilterParams();
	const [selectedScriptLog, setSelectedScriptLog] =
		React.useState<LogEvent>(null);

	const {
		data: scriptLogs,
		isLoading,
		isRefetching,
		fetchNextPage,
		isFetchingNextPage,
		hasNextPage,
		dataUpdatedAt,
		refetch,
	} = ScriptLogsAPI.usePaginatedScriptLogs(
		['scriptLogs', tenantId || tenantHostname, startDate, endDate, message],
		{
			minTimestamp: parseInt(startDate, 10),
			maxTimestamp: parseInt(endDate, 10),
			searchPhrase: message,
		},
		tenantId,
		authToken,
		tenantHostname,
	);

	const selectScriptLog = (requestId: string) => {
		const selected = scriptLogs?.pages
			.find((logResponse) =>
				logResponse.logEvents.find(
					(logEvent) => logEvent.requestId === requestId,
				),
			)
			?.logEvents.find((logEvent) => logEvent.requestId === requestId);
		setSelectedScriptLog(selected);
	};

	const handleDrawerCloser = (
		e: {},
		reason: 'backdropClick' | 'escapeKeyDown',
	) => {
		if (reason !== 'backdropClick') {
			setSelectedScriptLog(null);
		}
	};

	React.useEffect(() => {
		if (inView) {
			fetchNextPage();
		}
	}, [fetchNextPage, inView]);

	return (
		<Stack direction='column' height='100%' spacing={2}>
			<Typography variant='h3' gutterBottom>
				{SCRIPT_LOGS_HEADER()}
			</Typography>

			<FilterScriptLogs
				startDate={parseInt(startDate, 10)}
				endDate={parseInt(endDate, 10)}
				message={message}
				onApply={({ startDate, endDate, message }) => {
					const params = new URLSearchParams({});

					if (startDate) {
						params.set('startDate', startDate + '');
					}
					if (endDate) {
						params.set('endDate', endDate + '');
					}
					if (message) {
						params.set('message', message);
					}
					navigate(location.pathname + '?' + params.toString());
				}}
				refetch={refetch}
			/>

			{!isLoading && (
				<Typography variant='body2' align='right'>
					{isRefetching ? SCRIPT_LOGS_REFRESHING_LABEL() : null}
					{!isRefetching
						? SCRIPT_LOGS_LAST_UPDATED_LABEL() +
						  dayjs(dataUpdatedAt).local().format('lll')
						: null}
				</Typography>
			)}
			<Paper sx={{ flex: 1, overflowY: 'auto' }}>
				<TableContainer sx={{ maxHeight: '100%' }}>
					<Table size='small' stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{SCRIPT_LOGS_SUBHEADER_TIMESTAMP()}
									</Typography>
								</TableCell>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold' sx={{ flexGrow: 1 }}>
										{SCRIPT_LOGS_SUBHEADER()}
									</Typography>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading &&
								[...Array(3)].map((_, i) => <ScriptLogRowSkeleton key={i} />)}
							{!isLoading &&
								scriptLogs?.pages
									.map((logResponse) => logResponse.logEvents)
									.flat()
									.map((logEvent) => (
										<ScriptLogRow
											key={logEvent.requestId}
											selectScriptLog={selectScriptLog}
											selected={
												selectedScriptLog?.requestId === logEvent.requestId
											}
											{...logEvent}
										/>
									))}
							{!isLoading && (
								<TableRow ref={ref}>
									<TableCell colSpan={2} align='center'>
										<Typography>
											{isFetchingNextPage
												? SCRIPT_LOGS_FETCH_MORE_TEXT()
												: hasNextPage
												? SCRIPT_LOGS_HAS_MORE_TEXT()
												: SCRIPT_LOGS_NO_MORE_TEXT()}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
			<Drawer
				sx={{
					maxWidth: '1014px',
					flexShrink: 0,
					'& .MuiDrawer-paper': {
						width: '70vw',
						backgroundColor: 'white.light',
						p: 3,
						boxShadow:
							'-4px 4px 15px rgba(0, 0, 0, 0.2), -8px 8px 10px 1px rgba(0, 0, 0, 0.14)',
					},
				}}
				variant='temporary'
				anchor='right'
				open={!!selectedScriptLog}
				onClose={handleDrawerCloser}
			>
				{selectedScriptLog && (
					<ScriptLogsNested
						requestId={selectedScriptLog.requestId}
						timestamp={selectedScriptLog.timestamp}
						message={selectedScriptLog.message}
						tenantHostname={tenantHostname}
						handleClose={() => setSelectedScriptLog(null)}
					/>
				)}
			</Drawer>
		</Stack>
	);
}
